import React from "react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Check } from "lucide-react";

const today = () => new Date().toISOString().split("T")[0];

const OptionButton = ({ selected, onClick, children, testId, indicator = "radio" }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`fs-focus w-full text-left rounded-2xl border px-5 py-4 min-h-[60px] flex items-center justify-between gap-3
      transition-[background-color,border-color,box-shadow,transform] duration-200 active:scale-[0.99]
      ${selected
        ? "border-primary bg-primary text-primary-foreground shadow-[0_10px_30px_-12px_rgba(0,0,0,0.5)]"
        : "border-border bg-card text-foreground hover:border-foreground/40 hover:bg-secondary/60"}`}
  >
    <span className="text-base font-medium leading-snug">{children}</span>
    <span
      className={`shrink-0 h-5 w-5 border flex items-center justify-center transition-colors
        ${indicator === "check" ? "rounded-md" : "rounded-full"}
        ${selected ? "border-primary-foreground" : "border-border"}`}
    >
      {selected && (indicator === "check"
        ? <Check className="h-3.5 w-3.5" />
        : <span className="h-2.5 w-2.5 rounded-full bg-primary-foreground" />)}
    </span>
  </button>
);

export const QuestionInput = ({ question, value, onChange }) => {
  const t = question.type;

  if (t === "single") {
    return (
      <div className="flex flex-col gap-3" data-testid={`q-${question.id}`}>
        {question.options.map((opt, i) => (
          <OptionButton key={opt} testId={`opt-${question.id}-${i}`} selected={value === opt} onClick={() => onChange(opt)}>
            {opt}
          </OptionButton>
        ))}
      </div>
    );
  }

  if (t === "multiple") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => onChange(arr.includes(opt) ? arr.filter((o) => o !== opt) : [...arr, opt]);
    return (
      <div className="flex flex-col gap-3" data-testid={`q-${question.id}`}>
        {question.options.map((opt, i) => (
          <OptionButton key={opt} testId={`opt-${question.id}-${i}`} indicator="check" selected={arr.includes(opt)} onClick={() => toggle(opt)}>
            {opt}
          </OptionButton>
        ))}
      </div>
    );
  }

  if (t === "yesno") {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid={`q-${question.id}`}>
        <OptionButton testId={`opt-${question.id}-si`} selected={value === true} onClick={() => onChange(true)}>Sì</OptionButton>
        <OptionButton testId={`opt-${question.id}-no`} selected={value === false} onClick={() => onChange(false)}>No</OptionButton>
      </div>
    );
  }

  if (t === "scale5") {
    return (
      <div className="flex flex-col gap-3" data-testid={`q-${question.id}`}>
        {question.options.map((opt, i) => {
          const selected = value === opt;
          return (
            <button
              key={opt}
              type="button"
              data-testid={`opt-${question.id}-${i}`}
              onClick={() => onChange(opt)}
              className={`fs-focus w-full text-left rounded-2xl border px-4 py-4 min-h-[60px] flex items-center gap-3.5
                transition-[background-color,border-color,box-shadow,transform] duration-200 active:scale-[0.99]
                ${selected
                  ? "border-primary bg-primary text-primary-foreground shadow-[0_10px_30px_-12px_rgba(0,0,0,0.5)]"
                  : "border-border bg-card hover:border-foreground/40 hover:bg-secondary/60"}`}
            >
              <span className={`h-9 w-9 rounded-full grid place-items-center text-sm font-semibold shrink-0 border
                ${selected ? "border-primary-foreground" : "border-border text-muted-foreground"}`}>
                {i + 1}
              </span>
              <span className="text-base font-medium leading-snug">{opt}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (t === "scale") {
    const min = question.min ?? 1;
    const max = question.max ?? 10;
    const nums = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <div className="flex flex-col gap-4" data-testid={`q-${question.id}`}>
        <div className="grid grid-cols-5 gap-2.5">
          {nums.map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`scale-${question.id}-${n}`}
              onClick={() => onChange(n)}
              className={`fs-focus aspect-square rounded-xl border text-base font-semibold
                transition-[background-color,border-color,transform] duration-150 active:scale-95
                ${value === n ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-foreground/40"}`}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-xs text-muted-foreground px-0.5">
          <span>{question.min_label || min}</span>
          <span>{question.max_label || max}</span>
        </div>
      </div>
    );
  }

  if (t === "date") {
    return (
      <div data-testid={`q-${question.id}`}>
        <Input
          type="date"
          max={today()}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`input-${question.id}`}
          className="h-14 rounded-xl text-base"
        />
      </div>
    );
  }

  if (t === "number") {
    return (
      <div className="relative" data-testid={`q-${question.id}`}>
        <Input
          type="number"
          inputMode={question.integer ? "numeric" : "decimal"}
          step={question.step || "1"}
          min={question.min}
          max={question.max}
          value={value ?? ""}
          onChange={(e) => onChange(question.integer ? e.target.value.replace(/[^\d]/g, "") : e.target.value)}
          placeholder={question.integer ? "Es. 175" : "Inserisci un valore"}
          data-testid={`input-${question.id}`}
          className="h-14 rounded-xl text-base pr-16"
        />
        {question.unit && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium">
            {question.unit}
          </span>
        )}
      </div>
    );
  }

  if (t === "text_short") {
    return (
      <div data-testid={`q-${question.id}`}>
        <Input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder || "Scrivi qui…"}
          data-testid={`input-${question.id}`}
          className="h-14 rounded-xl text-base"
        />
      </div>
    );
  }

  // text_long
  return (
    <div data-testid={`q-${question.id}`}>
      <Textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.placeholder || "Scrivi qui…"}
        data-testid={`input-${question.id}`}
        className="min-h-[180px] rounded-xl text-base resize-none"
      />
    </div>
  );
};

// Follow-ups visible for a given parent question, based on current answers.
export function visibleFollowups(question, answers) {
  const fs = question.followups || [];
  const val = answers[question.id];
  return fs.filter((f) => (Array.isArray(val) ? val.includes(f.when) : val === f.when));
}

// Is a single answer sufficient?
export function isAnswered(question, value) {
  if (question.required === false) return true;
  switch (question.type) {
    case "yesno":
      return value === true || value === false;
    case "multiple":
      return Array.isArray(value) && value.length > 0;
    case "number": {
      if (value === "" || value === null || value === undefined || Number.isNaN(Number(value))) return false;
      const n = Number(value);
      if (question.min != null && n < question.min) return false;
      if (question.max != null && n > question.max) return false;
      return true;
    }
    case "single":
    case "scale5":
      return !!value;
    case "scale":
      return typeof value === "number";
    case "date":
    case "text_short":
    case "text_long":
      return !!(value && String(value).trim());
    default:
      return !!value;
  }
}

// A step (a top-level question + its currently visible follow-ups) is complete when
// the main answer and every visible required follow-up are answered.
export function stepComplete(question, answers) {
  if (!isAnswered(question, answers[question.id])) return false;
  for (const f of visibleFollowups(question, answers)) {
    if (!isAnswered(f, answers[f.id])) return false;
  }
  return true;
}

export default QuestionInput;
